import { Box, Button, Grid } from "@mui/material";
import { UserContext } from "../../hooks/UserContext";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { LoadingComponent } from "../helpers/LoadingComponent";
import { API, Branch, Deal, ProagricaTypes, Job, defaultJobForImport } from "../../api";
import { Oops } from "../../utilities/oops";
import ImportTableWithMap from "./ImportTable/ImportTableWithMap";
import { FeatureStats } from "./ImportTable";
import { Proagrica } from "../../utilities/integrations";
import { generateFileNames, ImportFormProps, itemSelector, loadItemsButton } from "./ImportHelpers";
import PortalTooltip from "../helpers/PortalTooltip";
import { FileOptionTypes, GeoJSONOption } from "../../utilities/types";

export function ProagricaImportForm(props: ImportFormProps) {
	const [growers, setGrowers] = useState<ProagricaTypes.Grower[]>([]);
	const [selectedGrowers, setSelectedGrowers] = useState<ProagricaTypes.Grower[]>([]);
	const [farms, setFarms] = useState<ProagricaTypes.GrowerFarm[]>([]);
	const [fields, setFields] = useState<ProagricaTypes.Field[]>([]);
	const [selectedFields, setSelectedFields] = useState<ProagricaTypes.Field[]>([]);
	const [samplingItems, setSamplingItems] = useState<any[]>([]);
	const [jobs, setJobs] = useState<Job[]>([]);
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);

	const [loading, setLoading] = useState<string | boolean>(false);
	const userContext = React.useContext(UserContext);

	//#region COMPONENT
	return (
		<>
			<Grid alignItems="stretch" alignContent="stretch" container maxWidth="md" margin="auto" spacing={1}>
				{/* Step 1: Log in if auth isn't available */}
				{!userContext?.proagrica_auth && (
					<Grid item xs={12}>
						<PortalTooltip title="Authenticate with your Proagrica/AGX account" placement="top">
							<Button fullWidth disabled={!!loading} variant="contained" color="primary" onClick={login}>
								Proagrica Login
							</Button>
						</PortalTooltip>
					</Grid>
				)}

				{userContext?.proagrica_auth &&
					!!props.selectedDeals.length &&
					loadItemsButton(!!loading, getGrowers, "Load Growers")}

				{growers.length > 0 &&
					itemSelector(
						!!loading,
						growers,
						selectedGrowers,
						handleGrowerChange,
						setSelectedGrowers,
						(option) => option.ID,
						(option) => option.Name,
						"Choose your grower(s)",
					)}

				{selectedGrowers.length > 0 && loadItemsButton(!!loading, getFields, "Load Fields")}

				{fields.length > 0 &&
					itemSelector(
						!!loading,
						fields,
						selectedFields,
						(event, value) => setSelectedFields(value),
						setSelectedFields,
						(field) => field.ID,
						(field) => `${field.GrowerName ? `${field.GrowerName} - ` : ""}${field.Name}`,
						"Choose your field(s)",
					)}

				{selectedFields.length > 0 && loadItemsButton(!!loading, getSamplingEvents, "Load Jobs")}

				{loading === "submit" && (
					<Box display="flex" justifyContent="center" alignItems="center">
						<LoadingComponent />
					</Box>
				)}
			</Grid>

			{jobs.length > 0 && (
				<>
					<ImportTableWithMap
						jobs={jobs}
						updateJobs={() => {}}
						deals={props.selectedDeals}
						allDeals={props.deals}
						setLoading={(task: string | boolean) => {
							setLoading(!!task);
						}}
						allOptions={{ Boundary: [], Zones: [], Centroids: [], Cores: [], Unknown: [] }}
						resetForm={resetForm}
						integration="Proagrica"
						user={userContext}
						featureStats={featureStats}
						allowMap={true}
						selectedCompany={props.selectedCompany ?? undefined}
						salespersons={props.salespersons ?? []}
						branches={props.branches ?? []}
						lastCropOptions={props.lastCropOptions}
						nextCropOptions={props.nextCropOptions}
					/>
				</>
			)}
		</>
	);
	//#endregion

	//#region FUNCTIONS
	function resetForm() {
		setJobs([]);
		setFeatureStats(undefined);
		setFields([]);
		setSelectedFields([]);
		setFarms([]);
		setSelectedGrowers([]);
		setGrowers([]);
	}

	async function getSamplingEvents() {
		setLoading(true);
		const loadingEvents = toast.loading("Loading Sampling Events...", { progress: 0 });

		// TODO this is a temporary hack to get the first deal to all growers
		// TODO this will be replaced by selection up above IF there is more than one selection
		const _growerDealMap: Record<string, Deal> = {};
		const _growerBranchMap: Record<string, Branch> = {};
		for (const grower of selectedGrowers) {
			_growerDealMap[grower.ID] = props.selectedDeals[0];
			if (props.selectedBranches.length > 0) {
				_growerBranchMap[grower.ID] = props.selectedBranches[0];
			}
		}

		try {
			setJobs([]);
			setFeatureStats(undefined);
			await API.Integrations.Proagrica.refreshToken();

			const newJobs: Job[] = [];
			const total = fields.length;
			let loaded = 0;
			for (const grower of selectedGrowers) {
				const deal = _growerDealMap[grower.ID];
				const growerFarms = farms.filter((farm) => farm.ParentID === grower.ID);

				for (const growerFarm of growerFarms) {
					const farm = await API.Integrations.Proagrica.getFarm(growerFarm.ID, grower.SyncID);
					const growerFields = fields.filter((field) => field.FarmID === growerFarm.ID);

					for (const field of growerFields) {
						if (!selectedFields.includes(field)) {
							continue;
						}
						if (loadingEvents)
							toast.update(loadingEvents, {
								render: `Loading Sampling Events for ${grower.Name} - ${field.Name ?? ""}...`,
								progress: loaded / total,
							});

						const s = await API.Integrations.Proagrica.getSamplingItems(field.ID, field.SyncID);
						samplingItems.push(...s);

						const fieldSamplingItems = samplingItems
							.filter((item) => item.FieldID === field.ID)
							.sort((a, b) => {
								return (b["ModifiedOn"] as string).localeCompare(a["ModifiedOn"]);
							});

						const { bndName, ptsName, znsName } = generateFileNames(grower.Name, farm.Name, field.Name);
						const bndGeoJSON = Proagrica.getBoundaryGeoJSON(field.CurrentBoundary.Records);
						const bndOption: GeoJSONOption = { name: bndName, geoJSON: bndGeoJSON };

						let znsGeoJSON: GeoJSON.FeatureCollection | undefined;
						let ptsGeoJSON: GeoJSON.FeatureCollection | undefined;
						let znsOptions: GeoJSONOption[] = [];
						let ptsOptions: GeoJSONOption[] = [];
						let isZoneJob = false;
						if (
							fieldSamplingItems &&
							fieldSamplingItems.length > 0 &&
							fieldSamplingItems[0].SoilSamplingRecords &&
							fieldSamplingItems[0].SoilSamplingRecords.length > 0
						) {
							isZoneJob = fieldSamplingItems[0].SoilSamplingRecords[0].WKT.toLowerCase().includes("polygon");
							const geoJSON = await Proagrica.getZoneOrPointsGeoJSON(fieldSamplingItems[0]);
							const option: GeoJSONOption = { name: isZoneJob ? znsName : ptsName, geoJSON: geoJSON };
							if (isZoneJob) {
								znsGeoJSON = geoJSON;
								znsOptions = [option];
							} else {
								ptsGeoJSON = geoJSON;
								ptsOptions = [option];
							}
						}

						const newJob: Job = defaultJobForImport(
							field.Name,
							deal.depth,
							deal.density,
							grower.Name,
							deal.frequencyOfMicros,
							deal.testPackage,
							farm.Name,
						);

						// Store GeoJSON directly - no SHP files needed!
						newJob.files = {
							pointsGeoJSON: ptsGeoJSON,
							boundaryGeoJSON: bndGeoJSON,
							zonesGeoJSON: znsGeoJSON,
						};

						newJob.fileOptions = {
							boundaryOptions: [{ type: FileOptionTypes.Boundary, options: [bndOption] }],
							boundaryGeoJSON: bndGeoJSON,
							pointsOptions: [
								{ type: FileOptionTypes.Centroids, options: isZoneJob ? [] : ptsOptions },
								{ type: FileOptionTypes.Cores, options: isZoneJob ? ptsOptions : [] },
							],
							pointsGeoJSON: ptsGeoJSON,
							zonesOptions: [{ type: FileOptionTypes.Zones, options: znsOptions }],
							zonesGeoJSON: znsGeoJSON,
						};
						newJob.dealIds = deal.id ? [deal.id] : [];
						newJob.labAccountNumber = getLabAccountNumber(deal, grower);
						newJob.fieldId = field.ID;
						newJob.fieldPriority = "Regular Turn (default)";
						newJob.submitterEmail = props.user?.email;
						newJob.submitterName = props.user?.name;
						newJob.branchIds = props.selectedBranches.map((branch) => branch.id);

						newJob.eventId = uuidv4(); // not sure why we're doing this... - DRB

						loaded++;
						if (loadingEvents)
							toast.update(loadingEvents, {
								render: `Loading Sampling Events for ${grower.Name} - ${field.Name ?? ""}...`,
								progress: loaded / total,
							});
						newJobs.push(newJob);
					}
				}
			}

			setFeatureStats({
				boundaries: newJobs.length,
				zones: newJobs.filter((job) => job.files.zonesGeoJSON?.features?.length).length,
				centroids: newJobs.filter((job) =>
					job.files.zonesGeoJSON?.features?.length ? false : job.files.pointsGeoJSON?.features?.length,
				).length,
				cores: newJobs.filter((job) =>
					job.files.zonesGeoJSON?.features?.length ? job.files.pointsGeoJSON?.features?.length : false,
				).length,
			});

			setJobs(newJobs);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.getSamplingEvents");
		} finally {
			setLoading(false);
			if (loadingEvents && toast.isActive(loadingEvents)) toast.dismiss(loadingEvents);
		}

		function getLabAccountNumber(deal: Deal, grower: ProagricaTypes.Grower) {
			const labAccountNumber_Deal = deal.labAccountNumber;
			let labAccountNumber = labAccountNumber_Deal;
			if (labAccountNumber_Deal?.toLowerCase().includes("branch")) {
				const branch = _growerBranchMap[grower.ID];
				if (branch) {
					const labAccountNumber_Branch = branch.labAccountNumber;
					if (labAccountNumber_Branch) {
						labAccountNumber = labAccountNumber_Branch;
					}
				}
			}
			return labAccountNumber;
		}
	}

	async function getFields() {
		setLoading(true);
		setFields([]);
		setSelectedFields([]);
		setJobs([]);
		await API.Integrations.Proagrica.refreshToken();

		try {
			// Phase 1: Collect all farms for all growers
			const loadingFarmsToast = toast.loading("Loading Farms...");
			const farmsWithGrowers = await API.Integrations.Proagrica.getFarmsForGrowers(selectedGrowers);
			setFarms(farmsWithGrowers.flatMap(({ farms }) => farms));
			if (loadingFarmsToast && toast.isActive(loadingFarmsToast)) toast.dismiss(loadingFarmsToast);

			// Phase 2: Process farms to get fields
			const total = selectedGrowers.length;
			let loaded = 0;
			const allFields: ProagricaTypes.Field[] = [];
			const loadingFieldsToast = toast.loading("Loading Fields...", { progress: 0 });

			for (const { farms, grower } of farmsWithGrowers) {
				if (loadingFieldsToast)
					toast.update(loadingFieldsToast, {
						render: `Loading Fields for ${grower.Name}...`,
						progress: loaded / total,
					});

				const fields = await API.Integrations.Proagrica.getFieldsForGrowerFarms(grower, farms);
				allFields.push(...fields);
				loaded++;
			}

			if (loadingFieldsToast && toast.isActive(loadingFieldsToast)) toast.dismiss(loadingFieldsToast);
			setFields(allFields);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.getFields");
		} finally {
			setLoading(false);
		}
	}

	function handleGrowerChange(event: any, growers: ProagricaTypes.Grower[]) {
		setFields([]);
		setSelectedFields([]);
		setJobs([]);
		setSamplingItems([]);
		setSelectedGrowers(growers);
	}

	async function getGrowers() {
		setLoading(true);
		await API.Integrations.Proagrica.refreshToken();
		const loadingGrowersToast = toast.loading("Loading Growers...");
		try {
			setFields([]);
			setSelectedFields([]);
			setJobs([]);
			setSamplingItems([]);
			setSelectedGrowers([]);

			const growers = await API.Integrations.Proagrica.getGrowers();
			setGrowers(growers);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.getGrowers");
		} finally {
			setLoading(false);
			if (loadingGrowersToast && toast.isActive(loadingGrowersToast)) toast.dismiss(loadingGrowersToast);
		}
	}

	async function login() {
		try {
			const authUrl = await API.Integrations.Proagrica.login(window.location.href);
			setLoading(true);
			window.location.href = authUrl;
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.login");
		}
	}
	//#endregion
}
